import Link from 'next/link';
import React from 'react';
import { formatDecimal } from '@app/common';
import { cn } from '../../../../../utils/tailwind';

type DropdownMenuItemType = {
    name?: string;
    url?: string;
    icon?: React.ReactNode;
    value?: number;
    selected?: boolean;
    onClick?: () => Promise<void> | void;
    currencyCode?: string;
    size?: 'small' | 'medium';
};

export const DropdownMenuItem: React.FC<DropdownMenuItemType> = ({
    name,
    url,
    onClick,
    selected = false,
    icon,
    value,
    currencyCode,
    size = 'medium',
}: DropdownMenuItemType) => {
    const wrapperClassName = cn(
        'group flex items-center border-t border-border text-text font-medium transition-colors first:border-t-0',
        size === 'small' ? 'px-0 py-xsmall' : 'px-[0.8rem] py-[1.2rem]',
        onClick || !url ? 'cursor-pointer' : undefined,
        selected && 'rounded-[10rem] border-transparent bg-[#EEF1F3] font-black [&+li]:border-t-transparent',
    );

    const iconClassName = cn(
        'mr-vsmall inline-flex items-center justify-center rounded-full bg-[#EEF1F3] transition-colors',
        'h-[3.2rem] w-[3.2rem] min-w-[3.2rem] md:h-[3.6rem] md:w-[3.6rem] md:min-w-[3.6rem]',
        'group-hover:bg-neutral-gray group-hover:[&_i]:text-text-inverse',
        '[&_i]:h-[1.4rem] [&_i]:w-[1.4rem] [&_i]:min-w-[1.4rem] [&_i]:transition-colors',
        'md:[&_i]:h-[1.8rem] md:[&_i]:w-[1.8rem] md:[&_i]:min-w-[1.8rem]',
        size === 'small' &&
            'h-[2.8rem] w-[2.8rem] min-w-[2.8rem] md:h-[3.2rem] md:w-[3.2rem] md:min-w-[3.2rem] [&_i]:h-[1.2rem] [&_i]:w-[1.2rem] [&_i]:min-w-[1.2rem] md:[&_i]:h-[1.4rem] md:[&_i]:w-[1.4rem] md:[&_i]:min-w-[1.4rem]',
        selected && 'bg-neutral-gray [&_i]:text-text-inverse',
    );

    const DropdownMenuItemStyled = () => (
        <div className="flex w-full items-center">
            {icon ? <div className={iconClassName}>{icon}</div> : null}
            <div
                className={cn(
                    'flex-1 whitespace-nowrap transition-colors group-hover:text-text-muted',
                    selected && 'text-text-muted',
                )}
            >
                {name}
            </div>
            {value !== undefined && (
                <div className="ml-medium text-text-muted">{`${formatDecimal(value)} ${currencyCode}`}</div>
            )}
        </div>
    );

    if (!url) {
        return (
            <li className={wrapperClassName} onClick={onClick}>
                <DropdownMenuItemStyled />
            </li>
        );
    }

    return (
        <li className={wrapperClassName}>
            <Link href={url} passHref>
                <a className="block w-full text-inherit">
                    <DropdownMenuItemStyled />
                </a>
            </Link>
        </li>
    );
};
